/**
 * Shared client-side helpers for the six marketing integrations added in
 * ROADMAP §2.2 / §2.3 (migration 57):
 *
 *   • amazon_ads   — Login with Amazon OAuth (advertising scopes)
 *   • google_ads   — Google OAuth (adwords scope)
 *   • meta_ads     — Facebook Login (ads_* scopes)
 *   • facebook     — Facebook Login (pages_* scopes)
 *   • instagram    — Facebook Login (instagram_* + pages_show_list scopes)
 *   • tiktok       — TikTok for Business OAuth (covers both ads + organic)
 *
 * They all share the same shape:
 *   1. The operator's app Client ID lives per-org on
 *      `integrations.metadata.client_id` (set via the matching
 *      `<provider>-set-app-credentials` Edge Function).
 *   2. Atlas writes a one-shot CSRF state token to sessionStorage, then
 *      redirects the browser to the platform's `/authorize` endpoint.
 *   3. The platform redirects back to
 *      `<atlas>/integrations/<provider>/callback`, where the matching
 *      callback page validates the state and posts the auth code to
 *      `<provider>-oauth-callback`.
 *
 * Why one shared file instead of six tiny ones:
 *   The Shopify / Amazon / GA4 / QBO helpers exist because each had unique
 *   per-platform quirks (Shopify needs `<shop>.myshopify.com` in the host;
 *   Amazon SP-API needs regional consent hosts + draft-app handling; GA4
 *   uses `prompt=consent`). The six marketing providers all use vanilla
 *   OAuth 2.0 against a global authorize endpoint, so they collapse onto
 *   a single `buildAuthorizeUrl` function with per-provider config.
 */

import type { IntegrationProvider } from '@/types/database';

/**
 * Subset of `IntegrationProvider` that maps to a marketing OAuth flow.
 * Excludes the financial / e-commerce providers (mercury / quickbooks /
 * shopify / amazon / plaid / ga4) which have their own dedicated helpers.
 */
export type MarketingOAuthProvider =
  | 'amazon_ads'
  | 'google_ads'
  | 'meta_ads'
  | 'facebook'
  | 'instagram'
  | 'tiktok';

export interface MarketingProviderConfig {
  /** Stable enum value persisted on `integrations.provider`. */
  id: MarketingOAuthProvider;
  /** Display name used in toasts + dialog copy. */
  label: string;
  /** Platform OAuth 2.0 authorize endpoint. */
  authorizeUrl: string;
  /**
   * Space- or comma-separated scope list. Each platform documents which
   * separator it expects — we just hand it through verbatim.
   *
   * NOTE on token-exchange: scope changes here require re-OAuth-ing
   * every connected account, because the access token's grant is
   * frozen at the moment the operator clicks "Allow". When you add a
   * new scope, bump this list and ship a friendly "reconnect required"
   * banner in the matching card.
   */
  scope: string;
  /** Separator the platform expects between scopes. */
  scopeDelimiter: ' ' | ',';
  /**
   * Path under `/integrations/...` where the platform redirects back.
   * Used to construct the `redirect_uri` query parameter; MUST also
   * be registered in the operator's app dashboard (each platform does
   * a strict string match).
   */
  callbackPath: string;
  /**
   * sessionStorage keys for the CSRF state token + any extra context
   * the callback page needs to validate the round-trip.
   */
  stateStorageKey: string;
  /**
   * Extra query parameters appended to the authorize URL beyond the
   * standard `client_id / response_type / scope / state / redirect_uri`
   * set. Lets us bake provider-specific bits (e.g. Google's
   * `access_type=offline`) in one place.
   */
  extraParams?: Record<string, string>;
  /**
   * Some providers prefer a non-standard parameter for the OAuth code
   * response (e.g. `app_id` instead of `client_id`). Default is
   * `client_id`; override here when needed.
   */
  clientIdParam?: string;
}

const PROVIDERS: Record<MarketingOAuthProvider, MarketingProviderConfig> = {
  // Amazon Ads uses Login-with-Amazon OAuth (the same identity stack as
  // Amazon SP-API, but a *different* app registration and a different
  // scope set). The advertising scopes only work for apps registered in
  // the Amazon Ads developer portal — not Seller Central → Develop Apps.
  amazon_ads: {
    id: 'amazon_ads',
    label: 'Amazon Ads',
    authorizeUrl: 'https://www.amazon.com/ap/oa',
    scope: 'advertising::campaign_management',
    scopeDelimiter: ' ',
    callbackPath: '/integrations/amazon-ads/callback',
    stateStorageKey: 'amazon_ads_oauth_state',
  },
  // Google Ads piggybacks on Google's regular OAuth 2.0 (same authorize
  // endpoint as GA4) but with the `adwords` scope. The Google Ads API
  // also requires a developer token — that goes on
  // `integrations.metadata.developer_token`, set via
  // `google-ads-set-app-credentials`.
  google_ads: {
    id: 'google_ads',
    label: 'Google Ads',
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    scope: 'https://www.googleapis.com/auth/adwords',
    scopeDelimiter: ' ',
    callbackPath: '/integrations/google-ads/callback',
    stateStorageKey: 'google_ads_oauth_state',
    extraParams: {
      // Same rationale as GA4 (ga4OAuth.ts): force the consent screen
      // every time so Google always returns a refresh token instead of
      // silently omitting it on repeat consent.
      access_type: 'offline',
      prompt: 'consent',
      include_granted_scopes: 'true',
    },
  },
  // Meta Marketing API uses Facebook Login. We pin the API version so a
  // future graph deprecation doesn't silently change the consent payload.
  // `ads_read` is read-only metrics; `ads_management` is required for
  // certain insight breakdowns; `business_management` lets us list the
  // business's ad accounts so the operator can pick one.
  meta_ads: {
    id: 'meta_ads',
    label: 'Meta Ads',
    authorizeUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    scope: 'ads_read,ads_management,business_management',
    scopeDelimiter: ',',
    callbackPath: '/integrations/meta-ads/callback',
    stateStorageKey: 'meta_ads_oauth_state',
  },
  // Facebook Pages (organic). Shares the FB Login endpoint with Meta Ads,
  // but a different scope set. `pages_show_list` lists the pages this
  // user manages; the other two unlock read-only post + insights access
  // for the picked page(s).
  facebook: {
    id: 'facebook',
    label: 'Facebook',
    authorizeUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    scope: 'pages_show_list,pages_read_engagement,pages_read_user_content',
    scopeDelimiter: ',',
    callbackPath: '/integrations/facebook/callback',
    stateStorageKey: 'facebook_oauth_state',
  },
  // Instagram Business is owned by Facebook pages, so the consent flow is
  // the FB Login endpoint with IG-specific scopes. `pages_show_list` is
  // still required because the operator picks the FB page their IG
  // business account is connected to.
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    authorizeUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    scope: 'instagram_basic,instagram_manage_insights,pages_show_list,pages_read_engagement',
    scopeDelimiter: ',',
    callbackPath: '/integrations/instagram/callback',
    stateStorageKey: 'instagram_oauth_state',
  },
  // TikTok for Business covers both their Marketing API (ads) and their
  // Display / Insights APIs (organic). We register a single TikTok app
  // and request the union of scopes; the same access token works for
  // both ad-account reporting and organic-post analytics.
  //
  // TikTok uses `app_id` (not `client_id`) on its authorize URL — that's
  // the only odd one out in the marketing six. Override via
  // `clientIdParam` so the rest of buildAuthorizeUrl stays generic.
  //
  // The `scope` value here is the *display* list shown to the operator;
  // TikTok actually negotiates the granular scope inside the consent
  // screen and returns the granted set on the redirect.
  tiktok: {
    id: 'tiktok',
    label: 'TikTok',
    authorizeUrl: 'https://business-api.tiktok.com/portal/auth',
    scope: 'user.info.basic,video.list,video.insights,advertiser.read,campaign.read,report.read',
    scopeDelimiter: ',',
    callbackPath: '/integrations/tiktok/callback',
    stateStorageKey: 'tiktok_oauth_state',
    clientIdParam: 'app_id',
  },
};

export function getMarketingProvider(id: MarketingOAuthProvider): MarketingProviderConfig {
  return PROVIDERS[id];
}

/**
 * Listed in the order they appear on /integrations so the cards render in
 * a predictable sequence regardless of insertion order in the table.
 */
export const MARKETING_PROVIDERS: MarketingProviderConfig[] = [
  PROVIDERS.amazon_ads,
  PROVIDERS.google_ads,
  PROVIDERS.meta_ads,
  PROVIDERS.facebook,
  PROVIDERS.instagram,
  PROVIDERS.tiktok,
];

/**
 * Returns the `redirect_uri` for a given provider, e.g.
 * `https://atlas.virro.com/integrations/meta-ads/callback`. This MUST be
 * registered exactly as-is in the operator's app dashboard.
 */
export function marketingRedirectUri(provider: MarketingOAuthProvider): string {
  return `${window.location.origin}${PROVIDERS[provider].callbackPath}`;
}

export interface StartMarketingOAuthArgs {
  /** Per-org app Client ID, from `integrations.metadata.client_id`. */
  clientId: string;
}

/**
 * Begins the OAuth flow by redirecting the browser to the platform's
 * authorize endpoint. Returns `null` on success (the caller's tab is
 * about to navigate away) and a human-readable error string if the input
 * is invalid. Callers should surface the error via toast.
 *
 * Idempotency: the CSRF state token is written to sessionStorage right
 * before the redirect. If the operator backs out, no cleanup is needed
 * — the next attempt overwrites the same key.
 */
export function startMarketingOAuth(
  provider: MarketingOAuthProvider,
  args: StartMarketingOAuthArgs,
): string | null {
  const cfg = PROVIDERS[provider];
  const clientId = args.clientId?.trim();
  if (!clientId) {
    return `No ${cfg.label} Client ID configured. Configure app credentials first.`;
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem(cfg.stateStorageKey, state);

  const url = new URL(cfg.authorizeUrl);
  url.searchParams.set(cfg.clientIdParam ?? 'client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', cfg.scope);
  url.searchParams.set('redirect_uri', marketingRedirectUri(provider));
  url.searchParams.set('state', state);
  for (const [k, v] of Object.entries(cfg.extraParams ?? {})) {
    url.searchParams.set(k, v);
  }

  window.location.href = url.toString();
  return null;
}

/**
 * Convenience predicate so the IntegrationsPage can ask "is this provider
 * a marketing-OAuth one?" without hard-coding the list in two places.
 */
export function isMarketingOAuthProvider(
  provider: IntegrationProvider,
): provider is MarketingOAuthProvider {
  return provider in PROVIDERS;
}
